import { useEffect, useState } from 'react';
import { get, post, errorMessage } from '../api';
import { Field, Spinner } from '../ui/controls';

interface ProofInfo { projectName: string; year: number; version: number; latest: boolean; decision: 'approved' | 'changes_requested' | null; decidedBy: string | null; decidedAt: string | null; expiresAt: string }

/**
 * Public customer proof page (no account): view the proof PDF, then approve
 * it for printing or ask for changes. The link itself is the credential.
 */
export function ProofPage({ token }: { token: string }) {
  const [info, setInfo] = useState<ProofInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const load = () => get<ProofInfo>(`/api/proof/${encodeURIComponent(token)}`).then(setInfo).catch((e) => setErr(errorMessage(e)));
  useEffect(() => { void load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (decision: 'approved' | 'changes_requested') => {
    setBusy(true); setFormErr(null);
    try { await post(`/api/proof/${encodeURIComponent(token)}/decision`, { decision, name, note }); await load(); }
    catch (e) { setFormErr(errorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="proof-page">
      <div className="card pad col" style={{ width: '100%', maxWidth: 560, gap: 14 }}>
        <div className="row"><img src="/favicon.svg" width={28} height={28} alt="" /><strong>Print Station · Calendar proof</strong></div>
        {err && <div className="notice err" role="alert">{err}</div>}
        {!info && !err && <Spinner label="Loading the proof…" />}
        {info && (
          <>
            <div>
              <h1 style={{ margin: 0 }}>{info.projectName}</h1>
              <span className="muted">{info.year} calendar · proof revision {info.version}</span>
            </div>
            <a className="btn primary" style={{ alignSelf: 'flex-start' }} href={`/api/proof/${encodeURIComponent(token)}/pdf`} target="_blank" rel="noopener">Open the proof (PDF)</a>
            <p className="small muted" style={{ margin: 0 }}>Check every page carefully: spelling, dates, phone numbers, email and web addresses, and photographs. Colours on screen are a guide only.</p>
            {info.decision ? (
              <div className={`notice ${info.decision === 'approved' ? 'ok' : 'warn'}`} role="status">
                {info.decision === 'approved' ? 'Approved for printing' : 'Changes requested'} by {info.decidedBy}{info.decidedAt ? ` on ${new Date(info.decidedAt).toLocaleString()}` : ''}. Thank you, we have been notified.
              </div>
            ) : !info.latest ? (
              <div className="notice warn" role="status">The artwork has been updated since this proof was sent. Please ask Print Station for the latest proof.</div>
            ) : (
              <form className="col" onSubmit={(e) => e.preventDefault()}>
                <Field label="Your name"><input className="input" value={name} autoComplete="name" onChange={(e) => setName(e.target.value)} /></Field>
                <Field label="Comments or changes needed"><textarea className="input" rows={4} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
                <label className="check"><input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />I have checked the proof and it is correct to print.</label>
                {formErr && <div className="notice err" role="alert">{formErr}</div>}
                <div className="row wrap">
                  <button type="button" className="btn primary" disabled={busy || !name.trim() || !checked} onClick={() => decide('approved')}>Approve for printing</button>
                  <button type="button" className="btn" disabled={busy || !name.trim() || !note.trim()} onClick={() => decide('changes_requested')}>Request changes</button>
                </div>
                <span className="small faint">{!name.trim() ? 'Enter your name to approve or request changes.' : !checked && !note.trim() ? 'Tick the box to approve, or describe the changes you need.' : ' '}</span>
              </form>
            )}
            <span className="small faint">This link is valid until {new Date(info.expiresAt).toLocaleDateString()}.</span>
          </>
        )}
      </div>
    </div>
  );
}
